import React, { useContext } from 'react';
import { AuthContext } from '@app/providers';
import {
  ArrowDown,
  DocTimeStamp,
  DocTitle,
  DocumentsContainer,
  UploadDocContainer,
  DocType,
  TitleHeader,
  DocumentTitleHeader,
  UploadBy,
  UploadedBy,
  UserName,
  DocumentDetailsContainer,
  ArrowUpIcon,
  DocumentTitleFooter,
  RemoveDocument,
  FooterSeperator,
  UploadedOn,
  DocTitleHead,
  DocTimeStampInner,
  UploadOnText,
  MainDiv,
  SubDiv,
  EmptyDiv,
  ViewDocument,
  View,
  ViewContainer,
  RemoveButtonContainer,
  DialogContainer,
  CancelRemove,
  DownloadDocument,
  UserEmail,
} from '@app/modules/transaction-form/documents-component';
import docLogo from '@app/components/icon/icons/doc-logo.svg';
import pdfLogo from '@app/components/icon/icons/pdf-logo.svg';
import excelLogo from '@app/components/icon/icons/excel-logo.svg';
import csvLogo from '@app/components/icon/icons/csv-logo.svg';
import pngLogo from '@app/components/icon/icons/png-logo.svg';
import jpegLogo from '@app/components/icon/icons/jpeg-logo.svg';
import Icon from '@app/components/icon/icon';
import moment from 'moment';
import { Button, Dialog } from '@app/components';
import { RemoveDoc, ShowDocType, UploadDocType } from '@app/types/documents';

type Props = {
  uploadedDocData: UploadDocType[];
  editDocuments?: any;
  allowEdit?: boolean;
  afterRemove?: any;
};

export default function UploadedDocuments({
  uploadedDocData,
  editDocuments,
  allowEdit,
  afterRemove,
}: Props) {
  const { userInfo }: any = useContext(AuthContext);
  const DOWNLOAD_URL = process.env.REACT_APP_DOWNLOAD_URL;
  const [showDialog, updateShowDialog] = React.useState(false);
  const [docs, updateDocs] = React.useState<UploadDocType[]>([]);
  const [viewDoc, updateViewDoc] = React.useState({ show: false, index: 0 });
  const [removingDocument, updateRemovingDocument] = React.useState<RemoveDoc>({
    document: [],
    index: 0,
  });
  let [showDocData, setShowDocData] = React.useState<ShowDocType>({
    index: 0,
    show: false,
    doc: [],
  });
  const logos: any = {
    'application/vnd.ms-excel': excelLogo,
    'application/pdf': pdfLogo,
    'application/msword': docLogo,
    'image/png': pngLogo,
    'text/csv': csvLogo,
    'image/jpeg': jpegLogo,
  };

  React.useEffect(() => {
    if (uploadedDocData && uploadedDocData.length) {
      updateDocs(uploadedDocData);
    }
  }, [uploadedDocData]);

  //showing document details
  function showDocDetails(document: UploadDocType, key: number) {
    setShowDocData({ index: key, doc: document, show: true });
  }

  //hiding document details
  function dontShowDetails() {
    setShowDocData({ show: false, index: 0, doc: [] });
  }
  function remove(doc: UploadDocType, key: number) {
    updateRemovingDocument({ document: doc, index: key });
    setTimeout(() => {
      updateShowDialog(true);
    }, 100);
  }
  //remove document from ui
  function removeDocument(doc: UploadDocType, index: number) {
    updateShowDialog(false);
    updateRemovingDocument({ document: [], index: 0 });
    const finalDocs: UploadDocType[] = docs.filter((f: UploadDocType) => {
      return f.docUniqueId !== doc.docUniqueId;
    });
    const removedDoc: UploadDocType[] = docs.filter(
      (document: UploadDocType) => {
        return document.docUniqueId === doc.docUniqueId;
      },
    );
    updateDocs(finalDocs);
    afterRemove(finalDocs, removedDoc);
    setShowDocData({ show: false, index: 0, doc: [] });
  }
  function editDocument(doc: UploadDocType, key: any) {
    setShowDocData({ show: false, index: 0, doc: [] });
    editDocuments(doc, key);
  }
  function viewDocument(doc: UploadDocType, index: number) {
    updateViewDoc({ show: true, index: index });
    viewS3Document(doc, updateViewDoc, index);
  }

  // A saved document records who uploaded it. The signed-in user is only the
  // right answer for a file that has just been picked and not yet saved, which
  // is the one case where `uploadedBy` is still empty.
  const currentUserName = (
    (userInfo && userInfo.firstName ? userInfo.firstName : '') +
    ' ' +
    (userInfo && userInfo.lastName ? userInfo.lastName : '')
  ).trim();

  function uploaderName(doc: UploadDocType) {
    return doc.uploadedBy || currentUserName || '--';
  }

  // Only the signed-in user's address is available on the client, so show it
  // only when they are in fact the uploader.
  function uploaderEmail(doc: UploadDocType) {
    const isCurrentUser =
      !doc.uploadedBy ||
      (userInfo && userInfo.firebaseId && doc.firebaseId === userInfo.firebaseId);
    return isCurrentUser && userInfo ? userInfo.email : '';
  }

  function checkDocContentType(contentType: string) {
    if (
      contentType &&
      (contentType === 'image/png' ||
        contentType === 'image/jpeg' ||
        contentType === 'application/pdf')
    ) {
      return true;
    }
    return false;
  }
  const docDetails = (doc: UploadDocType, key: number) => {
    return (
      <DocumentDetailsContainer>
        {showDialog && (
          <Dialog
            size="medium"
            title="Remove Document"
            isCenter={true}
            onClose={() => {
              updateShowDialog(false);
            }}>
            <DialogContainer>
              Are you sure you want to remove the document?
            </DialogContainer>
            <RemoveButtonContainer>
              <CancelRemove>
                <Button
                  variant="google"
                  onClick={() => updateShowDialog(false)}>
                  Cancel
                </Button>
              </CancelRemove>
              <Button
                variant="warning"
                onClick={() =>
                  removeDocument(
                    removingDocument.document,
                    removingDocument.index,
                  )
                }>
                Yes,Remove
              </Button>
            </RemoveButtonContainer>
          </Dialog>
        )}
        <ArrowUpIcon onClick={(e: any) => dontShowDetails()}>
          <Icon name="arrowUpIcon" />
        </ArrowUpIcon>
        <MainDiv>
          <ViewContainer>
            <EmptyDiv>
              <img src={logos[doc.docContentType]} alt="preview-img" />
            </EmptyDiv>
            {checkDocContentType(doc.docContentType) && !viewDoc.show ? (
              <ViewDocument onClick={() => viewDocument(doc, 0)}>
                <Icon name="viewIcon" />
                <View>View</View>
              </ViewDocument>
            ) : null}
            {viewDoc.show ? (
              <ViewDocument>
                <Icon name="loader" />
                <View>Loading...</View>
              </ViewDocument>
            ) : null}
            <DownloadDocument>
              <Icon name="downloadIcon" />
              <a
                href={`${DOWNLOAD_URL}downloadFile?key=${doc.docUrl}&docType=transDoc`}
                download>
                Download
              </a>
            </DownloadDocument>
          </ViewContainer>
          <SubDiv>
            <DocumentTitleHeader>
              <DocTitleHead>
                <TitleHeader>Title</TitleHeader>
                <DocTitle title={doc.title}>{doc.title}</DocTitle>
                <DocType>{doc.documentType}</DocType>
              </DocTitleHead>
              <UploadedOn>
                <UploadOnText>Uploaded On</UploadOnText>
                <DocTimeStampInner>
                  {moment(doc.uploadedDate).format('MMM D ,YYYY')} at{' '}
                  {moment(doc.uploadedDate).format('hh:mm A')}
                </DocTimeStampInner>
              </UploadedOn>
            </DocumentTitleHeader>
            <UploadBy>
              <UploadedBy>Uploaded By</UploadedBy>
              <UserName>{uploaderName(doc)}</UserName>
              <UserEmail>{uploaderEmail(doc)}</UserEmail>
            </UploadBy>
            <UploadBy>
              <UploadedBy>Notes</UploadedBy>
              <UserName>{doc.notes || '--'}</UserName>
            </UploadBy>
          </SubDiv>
        </MainDiv>
        {allowEdit && (
          <>
            <FooterSeperator />
            <DocumentTitleFooter>
              <RemoveDocument onClick={() => remove(doc, key)}>
                Remove
              </RemoveDocument>
              <Button
                onClick={() => editDocument(doc, key)}
                backgroundColor="#EFF3FA"
                color="#483311"
                variant="other">
                Edit
              </Button>
            </DocumentTitleFooter>
          </>
        )}
      </DocumentDetailsContainer>
    );
  };

  return (
    <div>
      {docs && docs.length
        ? docs.map((doc: any, key: number) => {
            return (
              <>
                {key === showDocData.index && showDocData.show ? (
                  docDetails(doc, key)
                ) : (
                  <UploadDocContainer onClick={() => showDocDetails(doc, key)}>
                    <DocumentsContainer>
                      <div>
                        <DocTitle>{doc.title}</DocTitle>
                        <DocTimeStamp>
                          Uploaded on{' '}
                          {moment(doc.uploadedDate).format('D MMM,YYYY')} by{' '}
                          {uploaderName(doc)}
                        </DocTimeStamp>
                      </div>
                      <ArrowDown onClick={() => showDocDetails(doc, key)}>
                        <Icon name="downArrow" />
                      </ArrowDown>
                    </DocumentsContainer>
                  </UploadDocContainer>
                )}
              </>
            );
          })
        : null}
    </div>
  );
}

export function viewS3Document(
  doc: UploadDocType,
  updateViewDoc: any,
  index: number,
) {
  // The documents bucket is private and blocks public access, so the browser
  // cannot read the object itself. It goes through the API, which signs the
  // request server-side and streams the file back — the same route the Download
  // link already uses.
  //
  // This previously called s3.getObject using credentials hardcoded in
  // providers/aws.tsx. That access key no longer exists in IAM, so every View
  // returned 403 and the `if (err) return err` swallowed it, leaving the
  // spinner running forever.
  //
  // The tab is opened synchronously, while still inside the click, because
  // opening it after the fetch resolves trips the popup blocker.
  const tab = window.open('', '_blank');
  const base = process.env.REACT_APP_DOWNLOAD_URL || '/';
  const url = `${base}downloadFile?key=${encodeURIComponent(
    doc.docUrl,
  )}&docType=transDoc`;

  fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error(`document fetch failed: ${res.status}`);
      return res.blob();
    })
    .then((body) => {
      const fileURL = URL.createObjectURL(
        new Blob([body], { type: doc.docContentType }),
      );
      updateViewDoc({ show: false, index: null });
      if (tab) {
        tab.location.href = fileURL;
      } else {
        window.open(fileURL);
      }
    })
    .catch(() => {
      // Clear the spinner instead of leaving it running.
      updateViewDoc({ show: false, index: null });
      if (tab) tab.close();
    });
}
